import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import classes from "./AddStaff.module.css";
import Header from "../../Components/Header/Header";
import Sidebar from "../../Components/Sidebar/Sidebar";
import {
    FaTimes, FaUserMd, FaUser, FaEnvelope, FaPhone, FaCalendarAlt,
    FaVenusMars, FaStethoscope, FaGraduationCap, FaIdCard, FaHeart,
    FaLanguage, FaFileAlt, FaSave, FaArrowLeft, FaUndo, FaUpload,
    FaTrash, FaPlus, FaTimesCircle, FaBriefcase, FaBuilding, FaUserTie,
    FaClock, FaMapMarkerAlt, FaGlobe, FaUsers, FaPhoneAlt, FaUserPlus,
    FaClipboardList, FaSyncAlt, FaSearch, FaChevronDown, FaUserNurse,
} from "react-icons/fa";
import { BASE_URL } from "../../Components/API/Api";

// ---------------------------------------------------------
// MONEY FORMATTERS
// ---------------------------------------------------------
const formatMoneyInput = (raw) => {
    if (raw === null || raw === undefined || raw === "") return "";
    const str = String(raw).replace(/,/g, "");
    if (str === "") return "";
    // Allow trailing decimal point while typing
    const [intPart, decPart] = str.split(".");
    const intClean = intPart.replace(/[^\d]/g, "");
    const formattedInt = intClean
        ? Number(intClean).toLocaleString("en-NG")
        : "";
    return decPart !== undefined
        ? `${formattedInt}.${decPart.replace(/[^\d]/g, "")}`
        : formattedInt;
};

const parseMoney = (val) => {
    if (val === null || val === undefined || val === "") return "";
    const cleaned = String(val).replace(/,/g, "").trim();
    const num = Number(cleaned);
    return Number.isFinite(num) ? num : "";
};

const formatNaira = (amount) => {
    const num = Number(amount || 0);
    return `₦${num.toLocaleString("en-NG", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
};

// --- SEARCHABLE SELECT ---
const SearchableSelect = ({
    name, value, onChange, options, placeholder = "Select option",
    disabled = false, loading = false,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const wrapperRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm("");
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    useEffect(() => {
        if (isOpen && inputRef.current) inputRef.current.focus();
    }, [isOpen]);

    const getOptionValue = (opt) => {
        if (opt === null || opt === undefined) return "";
        if (typeof opt === "object") return opt.value ?? opt.id ?? "";
        return opt;
    };

    const getOptionLabel = (opt) => {
        if (opt === null || opt === undefined) return "";
        if (typeof opt === "object") {
            return (
                opt.label ??
                opt.name ??
                opt.title ??
                String(opt.value ?? opt.id ?? "")
            );
        }
        return String(opt);
    };

    const filteredOptions = options.filter((opt) =>
        getOptionLabel(opt).toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (option) => {
        const rawVal = getOptionValue(option);
        const safeVal = rawVal === null || rawVal === undefined ? "" : String(rawVal);
        onChange({ target: { name, value: safeVal } });
        setIsOpen(false);
        setSearchTerm("");
    };

    const handleToggle = () => {
        if (disabled || loading) return;
        setIsOpen((prev) => !prev);
    };

    const handleClear = (e) => {
        e.stopPropagation();
        onChange({ target: { name, value: "" } });
        setSearchTerm("");
    };

    const selectedOption = options.find(
        (opt) => String(getOptionValue(opt)) === String(value)
    );
    const displayLabel = selectedOption
        ? getOptionLabel(selectedOption)
        : String(value || "");

    return (
        <div className={classes.searchableSelect} ref={wrapperRef}>
            <div
                className={`${classes.selectTrigger} ${isOpen ? classes.selectTriggerOpen : ""} ${(disabled || loading) ? classes.selectDisabled : ""}`}
                onClick={handleToggle}
            >
                <span className={displayLabel ? classes.selectValue : classes.selectPlaceholder}>
                    {loading ? "Loading..." : (displayLabel || placeholder)}
                </span>
                <div className={classes.selectIcons}>
                    {value && !disabled && !loading && (
                        <button type="button" className={classes.clearBtn} onClick={handleClear} tabIndex={-1}>
                            <FaTimes />
                        </button>
                    )}
                    <FaChevronDown className={`${classes.chevronIcon} ${isOpen ? classes.chevronRotate : ""}`} />
                </div>
            </div>

            {isOpen && !disabled && !loading && (
                <div className={classes.selectDropdown}>
                    <div className={classes.searchBox}>
                        <FaSearch className={classes.searchIcon} />
                        <input
                            ref={inputRef}
                            type="text"
                            placeholder="Search..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className={classes.searchInput}
                            onClick={(e) => e.stopPropagation()}
                        />
                        {searchTerm && (
                            <button type="button" className={classes.clearSearchBtn} onClick={() => setSearchTerm("")}>
                                <FaTimes />
                            </button>
                        )}
                    </div>
                    <div className={classes.optionsList}>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((opt, i) => {
                                const optValue = getOptionValue(opt);
                                const optLabel = getOptionLabel(opt);
                                return (
                                    <div
                                        key={`${optValue}-${i}`}
                                        className={`${classes.optionItem} ${String(optValue) === String(value) ? classes.optionSelected : ""}`}
                                        onClick={() => handleSelect(opt)}
                                    >
                                        {optLabel}
                                    </div>
                                );
                            })
                        ) : (
                            <div className={classes.noOptions}>No results found</div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

// --- SKELETON LOADERS ---
const SkeletonField = () => (
    <div className={classes.skeletonField}>
        <div className={classes.skeletonLabel}></div>
        <div className={classes.skeletonInput}></div>
    </div>
);
const SkeletonGrid3 = () => (
    <div className={classes.grid3}>
        {Array(3).fill(0).map((_, i) => <SkeletonField key={i} />)}
    </div>
);
const SkeletonSection = () => (
    <div className={classes.skeletonSection}>
        <div className={classes.skeletonTitle}></div>
        <div className={classes.skeletonContent}><SkeletonGrid3 /></div>
    </div>
);

// --- Enum normalize helper ---
const normalizeEnum = (val, allowed = []) => {
    if (val === null || val === undefined) return "";
    let str = val;
    if (typeof val === "object") {
        str = val.value ?? val.id ?? val.name ?? "";
    }
    str = String(str).trim().toLowerCase();
    const labelMap = {
        "active": "active",
        "inactive": "inactive",
        "on leave": "on_leave",
        "on_leave": "on_leave",
        "suspended": "suspended",
        "male": "male",
        "female": "female",
        "other": "other",
    };
    if (labelMap[str]) str = labelMap[str];
    if (allowed.length > 0 && !allowed.includes(str)) return "";
    return str;
};

// --- MEDICAL STAFF TYPES ---
const MEDICAL_STAFF_TYPES = [
    "Doctor", "Dentist", "Nurse", "Midwife", "Pharmacist",
    "Medical Laboratory Staff", "Radiology / Imaging Staff", "Physiotherapist",
    "Occupational Therapist", "Dietitian / Nutritionist", "Psychologist",
    "Optometrist", "Paramedic / Emergency Medical Staff", "Medical Social Worker",
    "Respiratory Therapist", "Anaesthesia Staff", "Public Health Staff",
    "Other Clinical Staff",
];

const TYPES_REQUIRING_LICENSE = [
    "Doctor", "Dentist", "Nurse", "Midwife", "Pharmacist",
    "Medical Laboratory Staff", "Radiology / Imaging Staff", "Physiotherapist",
    "Occupational Therapist", "Dietitian / Nutritionist", "Psychologist",
    "Optometrist", "Paramedic / Emergency Medical Staff", "Respiratory Therapist",
    "Anaesthesia Staff",
];

const TYPES_WITH_SPECIALIZATION = [
    "Doctor", "Dentist", "Nurse", "Midwife", "Pharmacist",
    "Medical Laboratory Staff", "Radiology / Imaging Staff", "Physiotherapist",
    "Psychologist", "Optometrist", "Anaesthesia Staff",
];

const AddStaff = () => {
    const navigate = useNavigate();
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const toggleSidebar = () => setSidebarOpen(!sidebarOpen);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState("");
    const [submitSuccess, setSubmitSuccess] = useState("");

    // Staff ID
    const [isGeneratingId, setIsGeneratingId] = useState(false);
    const [idGenerationError, setIdGenerationError] = useState("");

    // Lookups
    const [departments, setDepartments] = useState([]);
    const [isLoadingDepartments, setIsLoadingDepartments] = useState(false);
    const [departmentsError, setDepartmentsError] = useState("");

    const [designations, setDesignations] = useState([]);
    const [isLoadingDesignations, setIsLoadingDesignations] = useState(false);
    const [designationsError, setDesignationsError] = useState("");

    // Profile photo
    const [profilePhoto, setProfilePhoto] = useState(null);
    const [profilePhotoPreview, setProfilePhotoPreview] = useState("");

    // Form
    const [form, setForm] = useState({
        first_name: "",
        middle_name: "",
        last_name: "",
        email: "",
        phone_country_code: "+234",
        phone: "",
        phone_alternative: "",
        date_of_birth: "",
        gender: "",
        marital_status: "",
        nationality: "",
        employee_no: "",
        staff_category: "",
        staff_type: "",
        department_id: "",
        designation_id: "",
        reporting_to: "",
        employment_date: "",
        employment_type: "",
        work_shift: "",
        status: "",
        // Professional / medical fields
        qualification: "",
        specialization: "",
        sub_specialization: "",
        registration_body: "",
        license_number: "",
        registration_number: "",
        license_expiry_date: "",
        experience_years: "",
        consultation_fee: "",
        languages_spoken: "",
        about: "",
        // Contact
        address: "",
        city: "",
        state: "",
        country: "",
        postal_code: "",
        // Additional
        emergency_contact_name: "",
        emergency_relationship: "",
        emergency_phone_country_code: "+234",
        emergency_phone: "",
        notes: "",
    });

    // --- OPTIONS ---
    const staffCategories = [
        "Medical / Clinical Staff",
        "Non-Medical / Administrative Staff",
        "Support Staff",
    ];

    const staffTypesByCategory = {
        "Medical / Clinical Staff": [
            "Doctor", "Dentist", "Nurse", "Midwife", "Pharmacist",
            "Medical Laboratory Staff", "Radiology / Imaging Staff", "Physiotherapist",
            "Occupational Therapist", "Dietitian / Nutritionist", "Psychologist",
            "Optometrist", "Paramedic / Emergency Medical Staff", "Medical Social Worker",
            "Respiratory Therapist", "Anaesthesia Staff", "Public Health Staff",
            "Other Clinical Staff",
        ],
        "Non-Medical / Administrative Staff": [
            "Hospital Administration", "Human Resources", "Finance / Accounting",
            "Reception / Front Desk", "Medical Records / Health Information",
            "Procurement", "Customer Service", "Information Technology",
            "Legal Services", "Public Relations / Communications",
            "Secretarial / Executive Assistant", "Quality Assurance",
            "Research / Data Management", "Billing / Revenue", "Other Administrative Staff",
        ],
        "Support Staff": [
            "Security", "Cleaning / Environmental Services", "Maintenance",
            "Transport / Drivers", "Laundry / Linen Services", "Catering / Food Services",
            "Biomedical Engineering / Equipment Support", "Stores / Inventory",
            "Waste Management", "Porter / Patient Transport", "Mortuary Services",
            "Facilities Management", "Electrical Services", "Plumbing Services",
            "Other Support Staff",
        ],
    };

    const reportingToOptions = [
        "Chief Medical Director", "Medical Director", "Chief Nursing Officer",
        "Head of Department", "Senior Consultant", "Consultant",
        "Hospital Administrator", "Chief Accountant", "HR Manager", "IT Manager",
    ];

    const employmentTypes = [
        "Full-Time", "Part-Time", "Contract", "Temporary", "Casual",
        "Intern / Trainee", "Volunteer", "Locum", "Consultant",
    ];

    const workShifts = [
        "Day Shift", "Morning Shift", "Afternoon Shift", "Evening Shift",
        "Night Shift", "Rotating Shift", "Flexible", "Not Applicable",
    ];

    const statusOptions = [
        { value: "active", label: "Active" },
        { value: "inactive", label: "Inactive" },
        { value: "on_leave", label: "On Leave" },
        { value: "suspended", label: "Suspended" },
    ];

    const genderOptions = [
        { value: "male", label: "Male" },
        { value: "female", label: "Female" },
        { value: "other", label: "Other" },
    ];

    const maritalStatusOptions = ["Single", "Married", "Divorced", "Widowed", "Separated"];

    const qualifications = [
        "MBBS", "MD", "BDS", "BSc Nursing", "RN", "RM", "BPharm", "PharmD",
        "BMLS", "BSc Radiography", "BPT", "BOT", "BSc Dietetics",
        "BSc Psychology", "OD", "BSc Paramedic", "BSc Public Health", "MSc",
        "PhD", "Fellowship", "Diploma", "Certificate",
    ];

    const specializations = [
        "Cardiology", "Neurology", "Orthopedics", "Pediatrics", "Gynecology",
        "Dermatology", "Ophthalmology", "ENT", "Urology", "Nephrology",
        "Endocrinology", "Oncology", "Radiology", "Pathology", "Anesthesiology",
        "Psychiatry", "Dentistry", "Physiotherapy", "Emergency Medicine",
        "Family Medicine", "General Surgery", "Internal Medicine",
        "Obstetrics & Gynecology", "Pediatric Surgery", "Neurosurgery",
        "Cardiothoracic Surgery", "Plastic Surgery", "Public Health",
        "Community Medicine",
    ];

    const registrationBodies = [
        "MDCN - Medical and Dental Council of Nigeria",
        "NMCN - Nursing and Midwifery Council of Nigeria",
        "PCN - Pharmacy Council of Nigeria",
        "MLSCN - Medical Laboratory Science Council of Nigeria",
        "RRBN - Radiographers Registration Board of Nigeria",
        "MRTB - Medical Rehabilitation Therapists Board",
        "NCP - Nutrition Council of Nigeria",
        "NBC - Nigeria Bar Association",
        "ICAN - Institute of Chartered Accountants of Nigeria",
        "CIPM - Chartered Institute of Personnel Management",
        "CPN - Computer Professionals Registration Council",
        "Other",
    ];

    const relationships = [
        "Spouse", "Parent", "Sibling", "Child", "Relative", "Friend",
        "Colleague", "Other",
    ];

    const nationalities = [
        "Nigerian", "Ghanaian", "Kenyan", "South African", "British",
        "American", "Canadian", "Indian", "Chinese", "Other",
    ];

    const states = [
        "Abia", "Adamawa", "Akwa Ibom", "Anambra", "Bauchi", "Bayelsa",
        "Benue", "Borno", "Cross River", "Delta", "Ebonyi", "Edo", "Ekiti",
        "Enugu", "FCT - Abuja", "Gombe", "Imo", "Jigawa", "Kaduna", "Kano",
        "Katsina", "Kebbi", "Kogi", "Kwara", "Lagos", "Nasarawa", "Niger",
        "Ogun", "Ondo", "Osun", "Oyo", "Plateau", "Rivers", "Sokoto",
        "Taraba", "Yobe", "Zamfara",
    ];

    const countries = [
        "Nigeria", "Ghana", "Kenya", "South Africa", "United Kingdom",
        "United States", "Canada", "India", "China", "Other",
    ];

    const normalizeLookup = (rawList) => {
        if (!Array.isArray(rawList)) return [];
        return rawList
            .map((item) => {
                if (typeof item === "string") return { id: item, name: item };
                return {
                    id: item?.id ?? item?.uuid ?? item?.value ?? null,
                    name:
                        item?.name ||
                        item?.title ||
                        item?.department_name ||
                        item?.designation_name ||
                        item?.label ||
                        "",
                };
            })
            .filter((d) => d.id !== null && d.name);
    };

    const fetchDepartments = async () => {
        setIsLoadingDepartments(true);
        setDepartmentsError("");
        try {
            const companyId = parseInt(localStorage.getItem("companyId") || "1");
            const response = await axios.get(`${BASE_URL}/api/department`, {
                params: { company_id: companyId },
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            });
            const rawList =
                response.data?.data?.data ||
                response.data?.data ||
                response.data?.departments ||
                response.data;
            const list = normalizeLookup(rawList);
            setDepartments(list);
            if (list.length === 0) {
                setDepartmentsError("No departments found. Please add departments first.");
            }
        } catch (error) {
            let errMsg = "Failed to load departments.";
            if (error.response?.data?.message && typeof error.response.data.message === "string") {
                errMsg = error.response.data.message;
            } else if (error.request) {
                errMsg = "Unable to connect to server.";
            }
            setDepartmentsError(errMsg);
        } finally {
            setIsLoadingDepartments(false);
        }
    };

    const fetchDesignations = async () => {
        setIsLoadingDesignations(true);
        setDesignationsError("");
        try {
            const companyId = parseInt(localStorage.getItem("companyId") || "1");
            const response = await axios.get(`${BASE_URL}/api/designation`, {
                params: { company_id: companyId },
                headers: {
                    Accept: "application/json",
                    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                },
            });
            const rawList =
                response.data?.data?.data ||
                response.data?.data ||
                response.data?.designations ||
                response.data;
            const list = normalizeLookup(rawList);
            setDesignations(list);
            if (list.length === 0) {
                setDesignationsError("No designations found.");
            }
        } catch (error) {
            setDesignationsError("Unable to load designations. Please try again later.");
        } finally {
            setIsLoadingDesignations(false);
        }
    };

    const generateStaffId = async () => {
        setIsGeneratingId(true);
        setIdGenerationError("");
        try {
            const companyId = parseInt(localStorage.getItem("companyId") || "1");
            const response = await axios.get(
                `${BASE_URL}/api/staff/generate_number`,
                {
                    params: { company_id: companyId },
                    headers: {
                        Accept: "application/json",
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );
            const generatedId =
                response.data?.employee_no ||
                response.data?.staff_id ||
                response.data?.staff_number ||
                response.data?.data?.employee_no ||
                response.data?.data?.staff_id ||
                response.data?.data?.staff_number ||
                response.data?.data ||
                (typeof response.data === "string" ? response.data : null);

            if (generatedId) {
                setForm((prev) => ({ ...prev, employee_no: String(generatedId) }));
            } else {
                setIdGenerationError("Unexpected response format from server.");
            }
        } catch (error) {
            let errMsg = "Failed to generate Staff ID.";
            if (error.response?.data?.message && typeof error.response.data.message === "string") {
                errMsg = error.response.data.message;
            } else if (error.request) {
                errMsg = "Unable to connect to server.";
            }
            setIdGenerationError(errMsg);
        } finally {
            setIsGeneratingId(false);
        }
    };

    useEffect(() => {
        setTimeout(() => setIsLoading(false), 1000);
        generateStaffId();
        fetchDepartments();
        fetchDesignations();
    }, []);

    // --- CONDITIONAL LOGIC ---
    const isMedicalStaff = () =>
        form.staff_category === "Medical / Clinical Staff" &&
        MEDICAL_STAFF_TYPES.includes(form.staff_type);

    const requiresLicense = () =>
        isMedicalStaff() && TYPES_REQUIRING_LICENSE.includes(form.staff_type);

    const hasSpecialization = () =>
        isMedicalStaff() && TYPES_WITH_SPECIALIZATION.includes(form.staff_type);

    const showProfessionalInfo = isMedicalStaff;

    const getStaffTypes = () =>
        form.staff_category ? (staffTypesByCategory[form.staff_category] || []) : [];

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => {
            const updated = {
                ...prev,
                [name]: type === "checkbox" ? checked : value,
                ...(name === "staff_category" && { staff_type: "" }),
            };

            // Clear medical fields when leaving medical category
            if (name === "staff_type" && !MEDICAL_STAFF_TYPES.includes(value)) {
                updated.qualification = "";
                updated.specialization = "";
                updated.sub_specialization = "";
                updated.registration_body = "";
                updated.license_number = "";
                updated.registration_number = "";
                updated.license_expiry_date = "";
                updated.experience_years = "";
                updated.consultation_fee = "";
                updated.languages_spoken = "";
                updated.about = "";
            }

            if (name === "staff_category" && value !== "Medical / Clinical Staff") {
                updated.qualification = "";
                updated.specialization = "";
                updated.sub_specialization = "";
                updated.registration_body = "";
                updated.license_number = "";
                updated.registration_number = "";
                updated.license_expiry_date = "";
                updated.experience_years = "";
                updated.consultation_fee = "";
                updated.languages_spoken = "";
                updated.about = "";
            }

            return updated;
        });
        setSubmitError("");
        setSubmitSuccess("");
    };

    // ---------------------------------------------------------
    // CONSULTATION FEE HANDLER (formatted with commas)
    // ---------------------------------------------------------
    const handleConsultationFeeChange = (e) => {
        const formatted = formatMoneyInput(e.target.value);
        setForm((prev) => ({ ...prev, consultation_fee: formatted }));
        setSubmitError("");
        setSubmitSuccess("");
    };

    const handlePhotoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        if (file.size > 2 * 1024 * 1024) {
            Swal.fire({
                icon: "error", title: "File Too Large",
                text: "Profile photo must be less than 2MB", confirmButtonColor: "#2F80ED"
            });
            return;
        }
        if (!["image/png", "image/jpeg", "image/jpg"].includes(file.type)) {
            Swal.fire({
                icon: "error", title: "Invalid File Type",
                text: "Please upload PNG or JPG image", confirmButtonColor: "#2F80ED"
            });
            return;
        }
        setProfilePhoto(file);
        const reader = new FileReader();
        reader.onloadend = () => setProfilePhotoPreview(reader.result);
        reader.readAsDataURL(file);
    };

    const removePhoto = () => {
        setProfilePhoto(null);
        setProfilePhotoPreview("");
    };

    const handleReset = () => {
        setForm({
            first_name: "", middle_name: "", last_name: "", email: "",
            phone_country_code: "+234", phone: "", phone_alternative: "",
            date_of_birth: "", gender: "", marital_status: "", nationality: "",
            employee_no: "", staff_category: "", staff_type: "",
            department_id: "", designation_id: "", reporting_to: "",
            employment_date: "", employment_type: "", work_shift: "", status: "",
            qualification: "", specialization: "", sub_specialization: "",
            registration_body: "", license_number: "", registration_number: "",
            license_expiry_date: "", experience_years: "", consultation_fee: "",
            languages_spoken: "", about: "",
            address: "", city: "", state: "", country: "", postal_code: "",
            emergency_contact_name: "", emergency_relationship: "",
            emergency_phone_country_code: "+234", emergency_phone: "", notes: "",
        });
        setProfilePhoto(null);
        setProfilePhotoPreview("");
        setSubmitError("");
        setSubmitSuccess("");
        generateStaffId();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError("");
        setSubmitSuccess("");
        setIsSubmitting(true);

        const finalGender = normalizeEnum(form.gender, ["male", "female", "other"]);
        const finalStatus = normalizeEnum(form.status, ["active", "inactive", "on_leave", "suspended"]);

        // --- VALIDATION ---
        const errors = [];
        if (!form.first_name) errors.push("First Name is required");
        if (!form.last_name) errors.push("Last Name is required");
        if (!form.email) errors.push("Email Address is required");
        if (!form.phone) errors.push("Phone Number is required");
        if (!form.date_of_birth) errors.push("Date of Birth is required");
        if (!finalGender) errors.push("Gender is required (pick Male, Female or Other)");
        if (!form.staff_category) errors.push("Staff Category is required");
        if (!form.staff_type) errors.push("Staff Type is required");
        if (!form.designation_id) errors.push("Designation is required");
        if (!form.employment_date) errors.push("Employment Date is required");
        if (!form.employment_type) errors.push("Employment Type is required");
        if (!finalStatus) errors.push("Status is required");
        if (!form.address) errors.push("Address is required");
        if (!form.city) errors.push("City is required");
        if (!form.state) errors.push("State is required");
        if (!form.country) errors.push("Country is required");

        if (isMedicalStaff() && !form.qualification)
            errors.push("Professional Qualification is required");
        if (requiresLicense() && !form.license_number)
            errors.push("License / Registration Number is required");

        if (form.experience_years) {
            const expNum = parseFloat(form.experience_years);
            if (isNaN(expNum) || expNum < 1) {
                errors.push("Years of Experience must be at least 1");
            }
        }

        // Validate parsed consultation fee
        if (form.consultation_fee) {
            const feeNum = parseMoney(form.consultation_fee);
            if (feeNum === "" || isNaN(feeNum) || feeNum < 1) {
                errors.push("Consultation Fee must be at least 1");
            }
        }

        if (errors.length > 0) {
            Swal.fire({
                icon: "warning",
                title: "Please fix the following errors",
                html: `<div style="text-align: left; font-size: 14px;">${errors.map((e, i) => `${i + 1}. ${e}`).join("<br/>")}</div>`,
                confirmButtonColor: "#2F80ED",
            });
            setIsSubmitting(false);
            return;
        }

        try {
            const companyId = parseInt(localStorage.getItem("companyId") || "1");

            const fd = new FormData();
            fd.append("company_id", companyId);
            fd.append("employee_no", String(form.employee_no || ""));
            fd.append("first_name", form.first_name);
            if (form.middle_name) fd.append("middle_name", form.middle_name);
            fd.append("last_name", form.last_name);
            fd.append("email", form.email);
            if (form.phone_country_code) fd.append("phone_country_code", form.phone_country_code);
            fd.append("phone", form.phone);
            if (form.date_of_birth) fd.append("date_of_birth", form.date_of_birth);
            fd.append("gender", finalGender);
            if (form.marital_status) fd.append("marital_status", form.marital_status);
            if (form.nationality) fd.append("nationality", form.nationality);
            if (profilePhoto) fd.append("profile_photo", profilePhoto);

            if (form.department_id) fd.append("department_id", String(form.department_id));
            fd.append("designation_id", String(form.designation_id));
            fd.append("staff_type", form.staff_type);
            fd.append("staff_category", form.staff_category);
            fd.append("employment_date", form.employment_date);
            fd.append("employment_type", form.employment_type);
            if (form.work_shift) fd.append("work_shift", form.work_shift);
            fd.append("status", finalStatus);

            fd.append("address", form.address);
            fd.append("city", form.city);
            fd.append("state", form.state);
            fd.append("country", form.country);
            if (form.postal_code) fd.append("postal_code", form.postal_code);

            if (form.phone_alternative) fd.append("phone_alternative", form.phone_alternative);
            if (form.emergency_contact_name) fd.append("emergency_contact_name", form.emergency_contact_name);
            if (form.emergency_relationship) fd.append("emergency_relationship", form.emergency_relationship);
            if (form.emergency_phone_country_code) fd.append("emergency_phone_country_code", form.emergency_phone_country_code);
            if (form.emergency_phone) fd.append("emergency_phone", form.emergency_phone);
            if (form.notes) fd.append("notes", form.notes);

            // Professional / medical fields — only when medical staff
            if (isMedicalStaff()) {
                if (form.qualification) fd.append("qualification", form.qualification);
                if (form.specialization) fd.append("specialization", form.specialization);
                if (form.sub_specialization) fd.append("sub_specialization", form.sub_specialization);
                if (form.registration_body) fd.append("registration_body", form.registration_body);
                if (form.license_number) fd.append("license_number", form.license_number);
                if (form.registration_number) fd.append("registration_number", form.registration_number);

                if (form.experience_years) {
                    const expNum = parseFloat(form.experience_years);
                    if (!isNaN(expNum) && expNum >= 1) {
                        fd.append("experience_years", expNum);
                    }
                }

                // Parse formatted fee back to raw number before sending
                if (form.consultation_fee) {
                    const feeNum = parseMoney(form.consultation_fee);
                    if (feeNum !== "" && !isNaN(feeNum) && feeNum >= 1) {
                        fd.append("consultation_fee", feeNum);
                    }
                }

                if (form.languages_spoken) {
                    const langs = form.languages_spoken
                        .split(",")
                        .map((s) => s.trim())
                        .filter(Boolean);
                    langs.forEach((lang, i) => {
                        fd.append(`languages_spoken[${i}]`, lang);
                    });
                }

                if (form.about) fd.append("about", form.about);
            }

            await axios.post(
                `${BASE_URL}/api/staff/create`,
                fd,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                    },
                }
            );

            Swal.fire({
                icon: "success",
                title: "Success!",
                text: "Staff member added successfully!",
                confirmButtonColor: "#2F80ED",
                timer: 2000,
                timerProgressBar: true,
            });
            setSubmitSuccess("Staff member added successfully!");
            setTimeout(() => navigate("/staff"), 2000);

        } catch (error) {
            let errorMsg = "Failed to add staff member. Please try again.";
            if (error.response) {
                if (error.response.status === 401) {
                    errorMsg = "Unauthorized. Please login again.";
                } else if (error.response.status === 422 && error.response.data?.errors) {
                    const errs = error.response.data.errors;
                    const firstKey = Object.keys(errs)[0];
                    errorMsg = `${firstKey}: ${errs[firstKey][0]}`;
                } else if (error.response.data?.message) {
                    const message = error.response.data.message;
                    if (typeof message === "object") {
                        const firstKey = Object.keys(message)[0];
                        errorMsg = Array.isArray(message[firstKey])
                            ? message[firstKey][0]
                            : JSON.stringify(message);
                    } else if (typeof message === "string") {
                        errorMsg = message;
                    }
                }
            } else if (error.request) {
                errorMsg = "Unable to connect to server. Please check your internet connection.";
            }
            setSubmitError(errorMsg);
            Swal.fire({
                icon: "error",
                title: "Error!",
                text: errorMsg,
                confirmButtonColor: "#2F80ED",
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className={classes.pageContainer}>
                <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
                <div className={classes.mainArea}>
                    <Header toggleSidebar={toggleSidebar} />
                    <div className={classes.content}>
                        <div className={classes.skeletonContainer}>
                            <div className={classes.skeletonHeader}>
                                <div className={classes.skeletonTitle} style={{ width: "200px" }}></div>
                                <div className={classes.skeletonButton}></div>
                            </div>
                            <div className={classes.twoColSkeleton}>
                                <div className={classes.skeletonLeft}><SkeletonSection /></div>
                                <div className={classes.skeletonRight}><SkeletonSection /></div>
                            </div>
                            <SkeletonSection />
                            <SkeletonSection />
                            <SkeletonSection />
                            <SkeletonSection />
                            <div className={classes.skeletonFooter}>
                                <div className={classes.skeletonButton}></div>
                                <div className={classes.skeletonButton}></div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={classes.pageContainer}>
            <Sidebar isOpen={sidebarOpen} toggleSidebar={toggleSidebar} />
            <div className={classes.mainArea}>
                <Header toggleSidebar={toggleSidebar} />
                <div className={classes.content}>
                    <div className={classes.topHeader}>
                        <div className={classes.headerLeft}>
                            <div className={classes.headerIcon}><FaUsers /></div>
                            <div>
                                <h2>Add New Staff</h2>
                                <p>Create a new hospital staff record</p>
                            </div>
                        </div>
                        <button className={classes.closeBtn} onClick={() => navigate("/staff")}>
                            <FaTimes />
                        </button>
                    </div>

                    <form onSubmit={handleSubmit} className={classes.form} encType="multipart/form-data">
                        {submitSuccess && <div className={classes.successMessage}><span>{submitSuccess}</span></div>}
                        {submitError && <div className={classes.submitError}><span>{submitError}</span></div>}

                        <div className={classes.twoColLayout}>
                            <div className={classes.leftCol}>
                                <div className={classes.section}>
                                    <h3 className={classes.sectionTitle}>Profile Photo</h3>
                                    <div className={classes.photoUpload}>
                                        {profilePhotoPreview ? (
                                            <div className={classes.photoPreview}>
                                                <img src={profilePhotoPreview} alt="Profile" />
                                                <button type="button" className={classes.removePhotoBtn}
                                                    onClick={removePhoto} disabled={isSubmitting}>
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        ) : (
                                            <div className={classes.uploadArea}>
                                                <FaUpload className={classes.uploadIcon} />
                                                <p>Upload Photo</p>
                                                <span>JPG, JPEG, PNG (Max 2MB)</span>
                                                <input type="file" accept="image/png,image/jpeg,image/jpg"
                                                    onChange={handlePhotoChange} disabled={isSubmitting} />
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>

                            <div className={classes.rightCol}>
                                <div className={classes.section}>
                                    <h3 className={classes.sectionTitle}>Personal Information</h3>

                                    <div className={classes.grid3}>
                                        <div className={classes.inputGroup}>
                                            <label>First Name <span className={classes.required}>*</span></label>
                                            <input type="text" name="first_name" placeholder="Enter first name"
                                                className={classes.formInput} value={form.first_name}
                                                onChange={handleChange} disabled={isSubmitting} />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Middle Name</label>
                                            <input type="text" name="middle_name" placeholder="Enter middle name"
                                                className={classes.formInput} value={form.middle_name}
                                                onChange={handleChange} disabled={isSubmitting} />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Last Name <span className={classes.required}>*</span></label>
                                            <input type="text" name="last_name" placeholder="Enter last name"
                                                className={classes.formInput} value={form.last_name}
                                                onChange={handleChange} disabled={isSubmitting} />
                                        </div>
                                    </div>

                                    <div className={classes.grid2}>
                                        <div className={classes.inputGroup}>
                                            <label>Email Address <span className={classes.required}>*</span></label>
                                            <input type="email" name="email" placeholder="Enter email address"
                                                className={classes.formInput} value={form.email}
                                                onChange={handleChange} disabled={isSubmitting} />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Phone Number <span className={classes.required}>*</span></label>
                                            <div className={classes.phoneInput}>
                                                <span className={classes.phoneCode}>+234</span>
                                                <input type="text" name="phone" placeholder="Enter phone number"
                                                    className={classes.formInput} value={form.phone}
                                                    onChange={handleChange} disabled={isSubmitting} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className={classes.grid2}>
                                        <div className={classes.inputGroup}>
                                            <label>Date of Birth <span className={classes.required}>*</span></label>
                                            <input type="date" name="date_of_birth" className={classes.formInput}
                                                value={form.date_of_birth} onChange={handleChange} disabled={isSubmitting} />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Gender <span className={classes.required}>*</span></label>
                                            <SearchableSelect name="gender" value={form.gender}
                                                onChange={handleChange} options={genderOptions}
                                                placeholder="Select gender" disabled={isSubmitting} />
                                        </div>
                                    </div>

                                    <div className={classes.grid2}>
                                        <div className={classes.inputGroup}>
                                            <label>Marital Status</label>
                                            <SearchableSelect name="marital_status" value={form.marital_status}
                                                onChange={handleChange} options={maritalStatusOptions}
                                                placeholder="Select marital status" disabled={isSubmitting} />
                                        </div>
                                        <div className={classes.inputGroup}>
                                            <label>Nationality</label>
                                            <SearchableSelect name="nationality" value={form.nationality}
                                                onChange={handleChange} options={nationalities}
                                                placeholder="Select nationality" disabled={isSubmitting} />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={classes.section}>
                            <h3 className={classes.sectionTitle}><FaBriefcase /> Employment Information</h3>

                            <div className={classes.grid3}>
                                <div className={classes.inputGroup}>
                                    <label>
                                        Employee No
                                        {isGeneratingId && (
                                            <span style={{ marginLeft: 8, fontSize: 12, color: "#2F80ED" }}>
                                                <FaSyncAlt className={classes.spin} /> Generating...
                                            </span>
                                        )}
                                    </label>
                                    <div className={classes.staffIdWrapper}>
                                        <input type="text" name="employee_no"
                                            placeholder={isGeneratingId ? "Generating..." : "Auto generated"}
                                            className={classes.formInputReadOnly} value={form.employee_no} readOnly />
                                        <button type="button" className={classes.refreshIdBtn}
                                            onClick={generateStaffId} disabled={isGeneratingId || isSubmitting}
                                            title="Regenerate Employee No">
                                            <FaSyncAlt className={isGeneratingId ? classes.spin : ""} />
                                        </button>
                                    </div>
                                    {idGenerationError && <span className={classes.fieldError}>{idGenerationError}</span>}
                                </div>
                                <div className={classes.inputGroup}>
                                    <label>Staff Category <span className={classes.required}>*</span></label>
                                    <SearchableSelect name="staff_category" value={form.staff_category}
                                        onChange={handleChange} options={staffCategories}
                                        placeholder="Select staff category" disabled={isSubmitting} />
                                </div>
                                <div className={classes.inputGroup}>
                                    <label>Staff Type <span className={classes.required}>*</span></label>
                                    <SearchableSelect name="staff_type" value={form.staff_type}
                                        onChange={handleChange} options={getStaffTypes()}
                                        placeholder="Select staff type"
                                        disabled={isSubmitting || !form.staff_category} />
                                </div>
                            </div>

                            <div className={classes.grid3}>
                                <div className={classes.inputGroup}>
                                    <label>Department</label>
                                    <SearchableSelect name="department_id" value={form.department_id}
                                        onChange={handleChange} options={departments}
                                        placeholder="Select department"
                                        disabled={isSubmitting || isLoadingDepartments || departments.length === 0}
                                        loading={isLoadingDepartments} />
                                    {departmentsError && <span className={classes.fieldError}>{departmentsError}</span>}
                                </div>
                                <div className={classes.inputGroup}>
                                    <label>Designation <span className={classes.required}>*</span></label>
                                    <SearchableSelect name="designation_id" value={form.designation_id}
                                        onChange={handleChange} options={designations}
                                        placeholder="Select designation"
                                        disabled={isSubmitting || isLoadingDesignations || designations.length === 0}
                                        loading={isLoadingDesignations} />
                                    {designationsError && <span className={classes.fieldError}>{designationsError}</span>}
                                </div>
                                <div className={classes.inputGroup}>
                                    <label>Reporting To</label>
                                    <SearchableSelect name="reporting_to" value={form.reporting_to}
                                        onChange={handleChange} options={reportingToOptions}
                                        placeholder="Select reporting person" disabled={isSubmitting} />
                                </div>
                            </div>

                            <div className={classes.grid3}>
                                <div className={classes.inputGroup}>
                                    <label>Employment Date <span className={classes.required}>*</span></label>
                                    <input type="date" name="employment_date" className={classes.formInput}
                                        value={form.employment_date} onChange={handleChange} disabled={isSubmitting} />
                                </div>
                                <div className={classes.inputGroup}>
                                    <label>Employment Type <span className={classes.required}>*</span></label>
                                    <SearchableSelect name="employment_type" value={form.employment_type}
                                        onChange={handleChange} options={employmentTypes}
                                        placeholder="Select employment type" disabled={isSubmitting} />
                                </div>
                                <div className={classes.inputGroup}>
                                    <label>Work Shift</label>
                                    <SearchableSelect name="work_shift" value={form.work_shift}
                                        onChange={handleChange} options={workShifts}
                                        placeholder="Select work shift" disabled={isSubmitting} />
                                </div>
                            </div>

                            <div className={classes.grid3}>
                                <div className={classes.inputGroup}>
                                    <label>Status <span className={classes.required}>*</span></label>
                                    <SearchableSelect name="status" value={form.status}
                                        onChange={handleChange} options={statusOptions}
                                        placeholder="Select status" disabled={isSubmitting} />
                                </div>
                            </div>
                        </div>

                        {showProfessionalInfo() && (
                            <div className={classes.section}>
                                <h3 className={classes.sectionTitle}>
                                    {form.staff_type === "Nurse" || form.staff_type === "Midwife" ? (
                                        <><FaUserNurse /> Nursing / Professional Information</>
                                    ) : (
                                        <><FaStethoscope /> Professional Information</>
                                    )}
                                </h3>

                                <div className={classes.grid3}>
                                    <div className={classes.inputGroup}>
                                        <label>Professional Qualification <span className={classes.required}>*</span></label>
                                        <SearchableSelect name="qualification" value={form.qualification}
                                            onChange={handleChange} options={qualifications}
                                            placeholder="Select qualification" disabled={isSubmitting} />
                                    </div>

                                    {hasSpecialization() && (
                                        <div className={classes.inputGroup}>
                                            <label>Specialization</label>
                                            <SearchableSelect name="specialization" value={form.specialization}
                                                onChange={handleChange} options={specializations}
                                                placeholder="Select specialization" disabled={isSubmitting} />
                                        </div>
                                    )}

                                    {requiresLicense() && (
                                        <div className={classes.inputGroup}>
                                            <label>Registration Body</label>
                                            <SearchableSelect name="registration_body" value={form.registration_body}
                                                onChange={handleChange} options={registrationBodies}
                                                placeholder="Select registration body" disabled={isSubmitting} />
                                        </div>
                                    )}
                                </div>

                                <div className={classes.grid3}>
                                    {requiresLicense() && (
                                        <>
                                            <div className={classes.inputGroup}>
                                                <label>License Number <span className={classes.required}>*</span></label>
                                                <input type="text" name="license_number"
                                                    placeholder="Enter license number"
                                                    className={classes.formInput} value={form.license_number}
                                                    onChange={handleChange} disabled={isSubmitting} />
                                            </div>
                                            <div className={classes.inputGroup}>
                                                <label>Registration Number</label>
                                                <input type="text" name="registration_number"
                                                    placeholder="Enter registration number"
                                                    className={classes.formInput} value={form.registration_number}
                                                    onChange={handleChange} disabled={isSubmitting} />
                                            </div>
                                        </>
                                    )}

                                    <div className={classes.inputGroup}>
                                        <label>License Expiry Date</label>
                                        <input type="date" name="license_expiry_date"
                                            className={classes.formInput}
                                            value={form.license_expiry_date}
                                            onChange={handleChange} disabled={isSubmitting} />
                                    </div>
                                </div>

                                <div className={classes.grid3}>
                                    <div className={classes.inputGroup}>
                                        <label>Sub-Specialization</label>
                                        <input type="text" name="sub_specialization"
                                            placeholder="e.g., Interventional Cardiology"
                                            className={classes.formInput} value={form.sub_specialization}
                                            onChange={handleChange} disabled={isSubmitting} />
                                    </div>
                                    <div className={classes.inputGroup}>
                                        <label>Years of Experience</label>
                                        <input type="number" name="experience_years"
                                            placeholder="Enter years (min 1)" className={classes.formInput}
                                            value={form.experience_years} onChange={handleChange}
                                            min="1" step="1" disabled={isSubmitting} />
                                    </div>
                                    <div className={classes.inputGroup}>
                                        <label>Consultation Fee</label>
                                        <input type="text" name="consultation_fee"
                                            placeholder="e.g., 20,000.00"
                                            className={classes.formInput}
                                            value={form.consultation_fee}
                                            onChange={handleConsultationFeeChange}
                                            inputMode="decimal"
                                            disabled={isSubmitting} />
                                        {/* {form.consultation_fee && (
                                            <span className={classes.fieldHint}>
                                                {formatNaira(parseMoney(form.consultation_fee) || 0)}
                                            </span>
                                        )} */}
                                    </div>
                                </div>

                                <div className={classes.grid2}>
                                    <div className={classes.inputGroup}>
                                        <label>Languages Spoken</label>
                                        <input type="text" name="languages_spoken"
                                            placeholder="e.g., English, Yoruba, Igbo (comma-separated)"
                                            className={classes.formInput} value={form.languages_spoken}
                                            onChange={handleChange} disabled={isSubmitting} />
                                    </div>
                                </div>

                                <div className={classes.inputGroup}>
                                    <label>About</label>
                                    <textarea name="about"
                                        placeholder="Short bio or professional summary..."
                                        className={classes.formTextarea} value={form.about}
                                        onChange={handleChange} rows="3" disabled={isSubmitting} />
                                </div>
                            </div>
                        )}

                        <div className={classes.section}>
                            <h3 className={classes.sectionTitle}><FaMapMarkerAlt /> Contact & Address Information</h3>

                            <div className={classes.grid2}>
                                <div className={classes.inputGroup}>
                                    <label>Address <span className={classes.required}>*</span></label>
                                    <input type="text" name="address" placeholder="Enter full address"
                                        className={classes.formInput} value={form.address}
                                        onChange={handleChange} disabled={isSubmitting} />
                                </div>
                                <div className={classes.inputGroup}>
                                    <label>City <span className={classes.required}>*</span></label>
                                    <input type="text" name="city" placeholder="Enter city"
                                        className={classes.formInput} value={form.city}
                                        onChange={handleChange} disabled={isSubmitting} />
                                </div>
                            </div>

                            <div className={classes.grid3}>
                                <div className={classes.inputGroup}>
                                    <label>State <span className={classes.required}>*</span></label>
                                    <SearchableSelect name="state" value={form.state}
                                        onChange={handleChange} options={states}
                                        placeholder="Select state" disabled={isSubmitting} />
                                </div>
                                <div className={classes.inputGroup}>
                                    <label>Postal / ZIP Code</label>
                                    <input type="text" name="postal_code" placeholder="Enter postal code"
                                        className={classes.formInput} value={form.postal_code}
                                        onChange={handleChange} disabled={isSubmitting} />
                                </div>
                                <div className={classes.inputGroup}>
                                    <label>Country <span className={classes.required}>*</span></label>
                                    <SearchableSelect name="country" value={form.country}
                                        onChange={handleChange} options={countries}
                                        placeholder="Select country" disabled={isSubmitting} />
                                </div>
                            </div>
                        </div>

                        <div className={classes.section}>
                            <h3 className={classes.sectionTitle}><FaClipboardList /> Additional Information</h3>

                            <div className={classes.grid3}>
                                <div className={classes.inputGroup}>
                                    <label>Alternative Phone</label>
                                    <input type="text" name="phone_alternative" placeholder="Enter alternative phone"
                                        className={classes.formInput} value={form.phone_alternative}
                                        onChange={handleChange} disabled={isSubmitting} />
                                </div>
                                <div className={classes.inputGroup}>
                                    <label>Emergency Contact Name</label>
                                    <input type="text" name="emergency_contact_name" placeholder="Enter emergency contact"
                                        className={classes.formInput} value={form.emergency_contact_name}
                                        onChange={handleChange} disabled={isSubmitting} />
                                </div>
                                <div className={classes.inputGroup}>
                                    <label>Emergency Phone</label>
                                    <input type="text" name="emergency_phone" placeholder="Enter phone number"
                                        className={classes.formInput} value={form.emergency_phone}
                                        onChange={handleChange} disabled={isSubmitting} />
                                </div>
                            </div>

                            <div className={classes.grid2}>
                                <div className={classes.inputGroup}>
                                    <label>Relationship</label>
                                    <SearchableSelect name="emergency_relationship" value={form.emergency_relationship}
                                        onChange={handleChange} options={relationships}
                                        placeholder="Select relationship" disabled={isSubmitting} />
                                </div>
                            </div>

                            <div className={classes.inputGroup}>
                                <label>Notes</label>
                                <textarea name="notes" placeholder="Enter additional notes about this staff member..."
                                    className={classes.formTextarea} value={form.notes}
                                    onChange={handleChange} rows="4" disabled={isSubmitting} />
                            </div>
                        </div>

                        <div className={classes.footer}>
                            <div className={classes.note}>
                                All fields marked with <span className={classes.required}>*</span> are required.
                            </div>
                            <div className={classes.footerButtons}>
                                <button type="button" className={classes.backBtn}
                                    onClick={() => navigate("/staff")} disabled={isSubmitting}>
                                    <FaArrowLeft /> Back
                                </button>
                                <button type="button" className={classes.resetBtn}
                                    onClick={handleReset} disabled={isSubmitting}>
                                    <FaUndo /> Reset
                                </button>
                                <button type="submit" className={classes.saveBtn} disabled={isSubmitting}>
                                    <FaSave /> {isSubmitting ? "Saving..." : "Save Staff"}
                                </button>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default AddStaff;